import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Eyebrow, IMAGES, Reveal } from "./shared";

const Panel = ({ index, eyebrow, title, copy, cta, image, alt, testid, primary, href = "/#contact" }) => (
    <a
        href={href}
        data-testid={testid}
        className="group relative flex min-h-[32rem] items-end overflow-hidden lg:min-h-[40rem]"
    >
        <img
            src={image}
            alt={alt}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-night/95 via-night/45 to-night/10 transition-opacity duration-500 group-hover:from-night" />
        <span
            className="absolute right-6 top-6 font-serif text-lg font-medium tracking-[0.2em] text-ivory/50 lg:right-10 lg:top-10"
            aria-hidden="true"
        >
            {index}
        </span>
        <div className="relative w-full p-8 sm:p-10 lg:p-14">
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-champagne">{eyebrow}</p>
            <h3 className="mt-4 max-w-md font-serif text-3xl font-medium leading-tight tracking-tight text-ivory lg:text-4xl">
                {title}
            </h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-ivory/75">{copy}</p>
            <span
                className={`mt-8 inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.1em] transition-colors duration-300 ${
                    primary
                        ? "bg-ivory text-ink group-hover:bg-champagne"
                        : "border border-ivory/50 text-ivory group-hover:border-ivory group-hover:bg-ivory/10"
                }`}
            >
                {cta}
                <ArrowRight size={16} strokeWidth={1.5} />
            </span>
        </div>
    </a>
);

export const Audience = () => (
    <section id="audiences" data-testid="audience-section" className="bg-ivory pt-24 lg:pt-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <Reveal className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <Eyebrow>Who We Serve</Eyebrow>
                    <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        Where Can We Help You?
                    </h2>
                </div>
                <p className="max-w-sm text-sm leading-relaxed text-ink/60">
                    Two sides of the same promise — opportunity without borders.
                </p>
            </Reveal>
        </div>

        <Reveal className="mt-14">
            <div className="grid lg:grid-cols-2">
                <Panel
                    primary
                    index="01"
                    href="/recruitment"
                    testid="audience-employers-panel"
                    eyebrow="For Employers"
                    title="Build Your Workforce Beyond Borders"
                    copy="Source, screen and deploy qualified international manpower across major sectors — from first brief to arrival on site."
                    cta="Hire Talent"
                    image={IMAGES.employers}
                    alt="Confident African executive leading a business discussion"
                />
                <Panel
                    index="02"
                    href="/jobs"
                    testid="audience-professionals-panel"
                    eyebrow="For Professionals"
                    title="Your Next International Opportunity Starts Here"
                    copy="Connect with credible overseas employers and move through screening, documentation and relocation with a team beside you."
                    cta="Find Jobs"
                    image={IMAGES.professionals}
                    alt="Ambitious young African professional looking ahead with confidence"
                />
            </div>
        </Reveal>

        <a
            href="#global-mobility"
            data-testid="audience-students-strip"
            className="group block bg-night px-6 py-7 text-ivory transition-colors lg:px-10"
        >
            <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-6">
                <p className="text-sm leading-relaxed text-ivory/70">
                    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.28em] text-champagne sm:mb-0 sm:mr-5 sm:inline">
                        For Students & Travelers
                    </span>
                    Education, travel and global mobility opportunities — handled with the same care.
                </p>
                <span className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-ivory transition-colors group-hover:text-champagne">
                    Explore Services
                    <ArrowUpRight
                        size={16}
                        strokeWidth={1.5}
                        className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    />
                </span>
            </div>
        </a>
    </section>
);
